import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
// import { Calendar } from '@/components/ui/calendar';
// import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Calendar as CalendarIcon, TrendingUp, Users, GamepadIcon, Activity, UserCheck, Heart } from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AdminHeader from '../components/AdminHeader';
import { downloadUserActivityCsv } from '../lib/adminUserActivityCsv';
import { matchesRegistrationFilter, registrationCategories } from '../components/PushRegistrationEvidence';

import UserActivityExplorer from '../components/UserActivityExplorer';
import { sortExplorerRows, explorerPresets } from '../lib/userActivityExplorer';

const AdminAnalytics = () => {
  const { user: authUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [userActivityLoading, setUserActivityLoading] = useState(false);
  const [analyticsResult, setAnalyticsResult] = useState(null);
  const [reportResult, setReportResult] = useState(null);
  const contextRef = useRef(null);
  const identity = `${authUser?.id || ''}:${authUser?.role || ''}`;
  if (contextRef.current?.identity !== identity) contextRef.current = { identity };
  const context = contextRef.current;
  const data = analyticsResult?.context === context ? analyticsResult.value : null;
  const userActivityData = authUser?.role === 'admin' && reportResult?.context === context ? reportResult.value : null;
  const [activeTab, setActiveTab] = useState('overview');
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState('display_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [explorerPreset, setExplorerPreset] = useState('overview');
  const changeExplorerPreset = key => {
    setExplorerPreset(key);
    if (!['display_name', 'username', ...explorerPresets[key].keys].includes(userSort)) {
      setUserSort('display_name');
      setSortDirection('asc');
    }
  };
  const sortExplorer = key => {
    setUserSort(key);
    setSortDirection(key === userSort && sortDirection === 'desc' ? 'asc' : 'desc');
  };
  const [clubFilter, setClubFilter] = useState('all');
  const [hostFilter, setHostFilter] = useState('all');
  const [registrationFilter, setRegistrationFilter] = useState('all');
  const [followsNobody, setFollowsNobody] = useState(false);
  const [noFollowers, setNoFollowers] = useState(false);
  const [userActivityScope, setUserActivityScope] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date())
  });
  const [showAllStates, setShowAllStates] = useState(false);

  const accessDenied = !!authUser && (
    !['admin', 'scout'].includes(authUser.role) ||
    (activeTab === 'user_activity' && authUser.role !== 'admin')
  );
  useEffect(() => {
    setAnalyticsResult(null);
    setReportResult(null);
    setLoading(false);
    setUserActivityLoading(false);
    setClubFilter('all'); setHostFilter('all'); setRegistrationFilter('all'); setFollowsNobody(false); setNoFollowers(false);
  }, [identity]);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    if (!dateRange.from || !dateRange.to) return;

    const requestedContext = context;
    setLoading(true);
    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');

      const response = await apiClient.get(`/admin/analytics/summary?start=${startDate}&end=${endDate}`);
      if (contextRef.current === requestedContext) setAnalyticsResult({ context: requestedContext, value: response.data });
    } catch (error) {
      if (contextRef.current !== requestedContext) return;
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      if (contextRef.current === requestedContext) setLoading(false);
    }
  };

  const fetchUserActivity = async () => {
    if (!dateRange.from || !dateRange.to) return;
    if (authUser?.role !== 'admin') return;

    const requestedContext = context;
    setUserActivityLoading(true);
    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');

      const response = await apiClient.get(`/admin/analytics/users?start=${startDate}&end=${endDate}`);
      if (contextRef.current === requestedContext) setReportResult({ context: requestedContext, value: response.data, startDate, endDate });
    } catch (error) {
      if (contextRef.current !== requestedContext) return;
      console.error('Failed to fetch user activity:', error);
      toast.error('Failed to load user activity data');
    } finally {
      if (contextRef.current === requestedContext) setUserActivityLoading(false);
    }
  };

  const refreshActiveTab = () => {
    if (activeTab === 'user_activity') {
      fetchUserActivity();
    } else {
      fetchAnalytics();
    }
  };

  useEffect(() => {
    if (authUser?.role === 'admin') {
      fetchAnalytics();
    }
  }, [dateRange, authUser]);

  useEffect(() => {
    if (authUser?.role === 'admin' && activeTab === 'user_activity') {
      fetchUserActivity();
    }
  }, [dateRange, authUser, activeTab]);

  const setPreset = (preset) => {
    const today = new Date();
    switch (preset) {
      case 'today':
        setDateRange({ from: startOfDay(today), to: endOfDay(today) });
        break;
      case '7days':
        setDateRange({ from: startOfDay(subDays(today, 7)), to: endOfDay(today) });
        break;
      case '30days':
        setDateRange({ from: startOfDay(subDays(today, 30)), to: endOfDay(today) });
        break;
      default:
        break;
    }
  };

  // Show access denied message
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-6">
                You must be an admin to view this page.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Current role: <span className="font-medium">{authUser?.role || 'Not logged in'}</span>
              </p>
              <Button onClick={() => navigate('/dashboard')} className="w-full">
                Return to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while checking auth
  if (!authUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const stateData = showAllStates ? data?.state_breakdown_full : data?.state_breakdown;
  const currentLoading = activeTab === 'user_activity' ? userActivityLoading : loading;
  const userActivityRows = userActivityData?.users || [];
  const selectedRangeActivityFields = [
    'app_loads',
    'games_created',
    'games_joined',
    'games_confirmed',
    'follows_made',
    'followers_gained'
  ];
  const hasSelectedRangeActivity = (row) => (
    selectedRangeActivityFields.some((field) => (row[field] || 0) > 0)
  );
  const rangeStartLabel = dateRange.from ? format(dateRange.from, 'M/d/yyyy') : 'selected start';
  const rangeEndLabel = dateRange.to ? format(dateRange.to, 'M/d/yyyy') : 'selected end';
  const filteredUserActivityRows = userActivityRows
    .filter(row => clubFilter === 'all' || row.membership_club_ids?.includes(clubFilter))
    .filter(row => hostFilter === 'all' || row.membership_host_ids?.includes(hostFilter))
    .filter(row => !followsNobody || row.following_count === 0)
    .filter(row => !noFollowers || row.follower_count === 0)
    .filter(row => matchesRegistrationFilter(row.push_registration_report, registrationFilter))
    .filter((row) => {
      if (userActivityScope === 'active' && !hasSelectedRangeActivity(row)) return false;
      if (userActivityScope === 'no_activity' && !row.did_nothing_after_signup) return false;
      const query = userSearch.trim().toLowerCase();
      if (!query) return true;
      return (
        (row.display_name || '').toLowerCase().includes(query) ||
        (row.username || '').toLowerCase().includes(query)
      );
    });
  const sortedUserActivityRows = sortExplorerRows(filteredUserActivityRows, userSort, sortDirection, userActivityData?.membership_filter_options || {});

  const exportRangeMatches = reportResult?.startDate === format(dateRange.from, 'yyyy-MM-dd') &&
    reportResult?.endDate === format(dateRange.to, 'yyyy-MM-dd');
  const exportUserActivity = () => {
    if (authUser?.role !== 'admin' || contextRef.current !== context || !userActivityData ||
        currentLoading || !exportRangeMatches) return;
    // Use the complete filtered dataset, never DOM rows or a display-page slice.
    downloadUserActivityCsv(sortedUserActivityRows, {
      start: reportResult.startDate, end: reportResult.endDate,
      registrationObservedAt: userActivityData.registration_observed_at,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Admin Header */}
        <div className={activeTab === 'user_activity' ? 'overflow-x-auto' : undefined}><AdminHeader title="ANALYTICS" onRefresh={refreshActiveTab} refreshing={currentLoading} /></div>

        {/* Date Range Picker */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-gray-500" />
                <span className="font-medium">Date Range:</span>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPreset('today')}>
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPreset('7days')}>
                  Last 7 Days
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPreset('30days')}>
                  Last 30 Days
                </Button>
              </div>

              <div className="flex gap-2 items-center">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Start Date</label>
                  <input
                    type="date"
                    value={dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : ''}
                    onChange={(e) => setDateRange(prev => ({ ...prev, from: new Date(e.target.value) }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">End Date</label>
                  <input
                    type="date"
                    value={dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : ''}
                    onChange={(e) => setDateRange(prev => ({ ...prev, to: new Date(e.target.value) }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Help Text / Legend */}
            {activeTab !== 'user_activity' && <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-600 space-y-1">
                <p><strong>New Profiles</strong> = created in range • <strong>Returning Profiles</strong> = active in range but created earlier</p>
                <p><strong>Active Users</strong> = any user with an engagement event or login in range</p>
                <p><strong>Engagement Rate</strong> = events / active users • <strong>Profile Completed</strong> = DOB + Address Line 1 present</p>
              </div>
            </div>}
          </CardContent>
        </Card>

        <div className="mb-6 flex flex-wrap gap-2">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </Button>
          {authUser?.role === 'admin' && (
            <Button
              variant={activeTab === 'user_activity' ? 'default' : 'outline'}
              onClick={() => setActiveTab('user_activity')}
            >
              User Activity
            </Button>
          )}
        </div>

        {currentLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading analytics...</p>
          </div>
        ) : activeTab === 'overview' && data ? (
          <>
            {/* KPI Cards - Games */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Games Created</CardTitle>
                  <GamepadIcon className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.games.games_created}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Games Completed</CardTitle>
                  <GamepadIcon className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.games.games_completed}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Players Joining Games</CardTitle>
                  <Users className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.games.players_joining}</div>
                </CardContent>
              </Card>
            </div>

            {/* KPI Cards - Profiles */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">New Profiles</CardTitle>
                  <UserCheck className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.profiles.new_profiles}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Returning Profiles</CardTitle>
                  <Users className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.profiles.returning_profiles}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Active Profiles</CardTitle>
                  <Activity className="h-4 w-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.profiles.active_profiles}</div>
                  <p className="text-xs text-gray-500 mt-1">of {data.summary.profiles.total_profiles} total</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">% Profiles Completed</CardTitle>
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.profiles.profile_completion_rate}%</div>
                  <p className="text-xs text-gray-500 mt-1">Has DOB + Address</p>
                </CardContent>
              </Card>
            </div>

            {/* KPI Cards - Sessions & Social */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Logins</CardTitle>
                  <Activity className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.sessions.total_logins}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Follows</CardTitle>
                  <Heart className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.social.total_follows}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Users Following</CardTitle>
                  <Users className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.social.users_following}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Users Followed</CardTitle>
                  <Users className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.social.users_being_followed}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Engagement Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.summary.engagement.engagement_rate}</div>
                  <p className="text-xs text-gray-500 mt-1">events/active user</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Games Created vs Completed</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.time_series.games}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="games_created" fill="#3b82f6" name="Created" />
                      <Bar dataKey="games_completed" fill="#10b981" name="Completed" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Active Users Per Day</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={data.time_series.active_users}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="active_users" stroke="#8b5cf6" strokeWidth={2} name="Active Users" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* State Breakdown Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Breakdown By State</CardTitle>
                  {data.state_breakdown_full.length > 10 && (
                    <Button variant="outline" size="sm" onClick={() => setShowAllStates(!showAllStates)}>
                      {showAllStates ? 'Show Top 10' : `Show All (${data.state_breakdown_full.length})`}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>State</TableHead>
                      <TableHead className="text-right">Active Users</TableHead>
                      <TableHead className="text-right">Games Created</TableHead>
                      <TableHead className="text-right">Games Completed</TableHead>
                      <TableHead className="text-right">Players Joining</TableHead>
                      <TableHead className="text-right">% Profiles Done</TableHead>
                      <TableHead className="text-right">Engagement Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stateData && stateData.length > 0 ? (
                      stateData.map((state) => (
                        <TableRow key={state.state}>
                          <TableCell className="font-medium">{state.state}</TableCell>
                          <TableCell className="text-right">{state.active_users}</TableCell>
                          <TableCell className="text-right">{state.games_created}</TableCell>
                          <TableCell className="text-right">{state.games_completed}</TableCell>
                          <TableCell className="text-right">{state.players_joining}</TableCell>
                          <TableCell className="text-right">{state.profile_completion_rate}%</TableCell>
                          <TableCell className="text-right">{state.engagement_rate}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-500">
                          No state data available for this date range
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : activeTab === 'user_activity' ? (
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle>User Activity Detail</CardTitle>
                  <Button type="button" className="mt-2" onClick={exportUserActivity}
                    disabled={!userActivityData || currentLoading || !exportRangeMatches}>
                    Export filtered users CSV
                  </Button>
                  <p className="mt-1 text-xs text-gray-500">One row per matching user across all pages; no device details. Native usage and permission are Not collected. Recorded product evidence counts stored actions and memberships, not attendance. App loads include web activity; registration eligibility is a read-time observation.</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {userActivityScope === 'active' && `Showing users with activity from ${rangeStartLabel} to ${rangeEndLabel}.`}
                    {userActivityScope === 'all' && `Showing all users with activity counts from ${rangeStartLabel} to ${rangeEndLabel}.`}
                    {userActivityScope === 'no_activity' && 'Showing accounts with no recorded product evidence in the counted fields.'}
                  </p>

                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="flex rounded-md border bg-white p-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={userActivityScope === 'active' ? 'default' : 'ghost'}
                      onClick={() => setUserActivityScope('active')}
                    >
                      Active in Range
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={userActivityScope === 'no_activity' ? 'default' : 'ghost'}
                      onClick={() => setUserActivityScope('no_activity')}
                    >
                      No Counted Records
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={userActivityScope === 'all' ? 'default' : 'ghost'}
                      onClick={() => setUserActivityScope('all')}
                    >
                      All Users
                    </Button>
                  </div>
                  <input
                    type="search"
                    value={userSearch}
                    onChange={(event) => setUserSearch(event.target.value)}
                    placeholder="Search users"
                    className="min-w-[220px] rounded-md border px-3 py-2 text-sm"
                  />

                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="user-activity-filters mb-3 text-xs text-gray-500">
                <label className="block mb-2">Club{' '}
                  <select aria-label="Club filter" value={clubFilter} onChange={event => setClubFilter(event.target.value)} className="border rounded p-2">
                    <option value="all">All clubs / accounts</option>
                    {(userActivityData?.membership_filter_options?.clubs || []).map(club => <option key={club.id} value={club.id}>{club.name}</option>)}
                  </select>
                </label>
                <label className="block mb-2">Trainer / club host{' '}
                  <select aria-label="Trainer / club host filter" value={hostFilter} onChange={event => setHostFilter(event.target.value)} className="border rounded p-2">
                    <option value="all">All hosts / accounts</option>
                    {(userActivityData?.membership_filter_options?.hosts || []).map(host => <option key={host.id} value={host.id}>{host.name}</option>)}
                  </select>
                </label>


                <label className="block mb-2"><input type="checkbox" aria-label="Follows nobody" checked={followsNobody} onChange={event => setFollowsNobody(event.target.checked)} /> Follows nobody</label>
                <label className="block mb-2"><input type="checkbox" aria-label="No followers" checked={noFollowers} onChange={event => setNoFollowers(event.target.checked)} /> No followers</label>

                <label className="block mb-2">Registration filter{' '}
                  <select aria-label="Registration filter" value={registrationFilter} onChange={event => setRegistrationFilter(event.target.value)} className="border rounded p-2">
                    <option value="all">All registrations / accounts</option>
                    <option value="excluded">Accounts with active excluded registrations</option>
                    {Object.entries(registrationCategories).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                </label>



                <div className="explorer-filter-result">Showing {filteredUserActivityRows.length} of {userActivityRows.length} users</div>
                <details className="explorer-definitions"><summary>Report definitions and evidence limits</summary><p>Club and host filters include actual accepted members of the selected club or that host’s clubs, including supported legacy memberships. Pending invitations and account roles do not establish membership.</p><p>Active in range means a recorded app load, creation, roster join, confirmation, or accepted follow in the selected range. The no-counted-records filter uses the existing sum of retained creation, roster, waitlist, confirmation, comment, relationship, membership and invitation records across all time; it does not establish attendance or return behavior.</p><p>Following and follower counts are distinct current accepted connections to existing accounts, independent of the date range. Follows made and followers gained count currently retained accepted relationship records created in the selected range; removed relationships are not historical events in this report.</p><p>DOB present and Address present are presence checks only; they do not validate values or establish participation eligibility.</p><p>Authenticated native usage observations are not collected. Account/device-bound permission reporting is not collected. Registration records do not establish installations, permission, or prior delivery.</p><p>Observed: {userActivityData?.registration_observed_at || 'Unavailable'}. Eligibility is a read-time observation, not a delivery guarantee; dispatch limits and other notification policies still apply. Counts describe registrations; an account can appear in multiple categories. Activity and search filters also apply.</p></details>
              </div>
              <UserActivityExplorer rows={sortedUserActivityRows} preset={explorerPreset} setPreset={changeExplorerPreset} sortKey={userSort} direction={sortDirection} onSort={sortExplorer} options={userActivityData?.membership_filter_options || {}} observedAt={userActivityData?.registration_observed_at} />
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Select a date range to view analytics
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
